import { useAxios } from '@/hooks/useAxios';
import { useAuth } from '@/state/auth';
import { Entitlement, UniqueQueryKeys, WhoamiResponse } from '@/types';
import { useQuery } from '@/utils/api';
import { RoleName } from '@/utils/role.util';

const ROLE_RANK: Record<RoleName, number> = {
  readonly: 0,
  analyst: 1,
  admin: 2,
};

export const roleHelpers = {
  isAtLeast(userRole: RoleName, requiredRole: RoleName): boolean {
    return (ROLE_RANK[userRole] ?? -1) >= (ROLE_RANK[requiredRole] ?? -1);
  },
};

/**
 * TanStack Query hook for the GET /whoami endpoint.
 *
 * Fetches the current user's identity, role, and entitlements.
 * Cached per tenant (invalidates when `friend` changes due to impersonation).
 *
 * @returns Query result with `data` as WhoamiResponse and a `hasEntitlement` helper.
 *
 * @example
 * ```tsx
 * const { data, hasEntitlement } = useWhoami();
 *
 * if (hasEntitlement('manage_accounts')) {
 *   // Show admin-only UI
 * }
 * ```
 */
export function useWhoami() {
  const axios = useAxios();
  const { friend, me } = useAuth();

  const query = useQuery<WhoamiResponse>({
    queryKey: [UniqueQueryKeys.WHOAMI, friend || me],
    queryFn: async () => {
      const { data } = await axios.get<WhoamiResponse>('/whoami');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — entitlements rarely change mid-session
    defaultErrorMessage: 'Failed to fetch user identity',
  });

  /**
   * Check if the current user has a specific entitlement.
   *
   * Returns `false` while the query is loading (fail-safe).
   */
  function hasEntitlement(entitlement: Entitlement): boolean {
    if (!query.data?.entitlements) {
      return false;
    }
    return query.data.entitlements.includes(entitlement);
  }

  return {
    ...query,
    hasEntitlement,
  };
}
